// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

'use strict';

import { TokenManager, TokenManagerFactory } from '@protolabs/enterprise-token-manager';
import tokenManagerConfig from '@/tokenManagerConfig';
import cookie from 'js-cookie';
import { getQueryStringValue } from '@/utils/queryUtils';
import { ClaimTypes } from '@/models/constants';

// allows us to split the guest/main flow without extra custom code
// vite allows us to dynamically import these modules as an async method
// and will only render one or the other depending on which gets called
const guestInit = async () => await import('./guest');
const mainInit = async () => await import('./main');

/**
 * Invoke on a redirect handshake back to commerce.
 */
const completeAuth = () => {
	const tokenManager = TokenManagerFactory.getOrCreateTokenManager(tokenManagerConfig);
	tokenManager
		.completeAuthenticationAsync()
		.then((activeRoute) => {
			window.location.replace(activeRoute);
		})
		.catch((error) => {
			console.error(error);
			window.location.replace('/');
		});
};

const demoKey = 'demo';
const continueAsGuestKey = 'cag';

export const runSignInFlow = (tokenManager: TokenManager) => {
	const lang = cookie.get('locale');
	const getaquote = getQueryStringValue('getaquote');

	const currentLocation = new URL(decodeURIComponent(window.location.href));
	const cagValue = currentLocation.searchParams.get(continueAsGuestKey);

	const extraLoginParams = {
		...(window.isPublic ? { [continueAsGuestKey]: cagValue ?? '1' } : {}),
		...(window.isDemoQuote ? { [demoKey]: true } : {}),
	};

	const options = {
		...(lang ? { lang } : {}),
		...(getaquote ? { getaquote } : {}),
		...extraLoginParams,
	};

	return tokenManager.startAuthenticationAsync(options);
};

export const runAuthFlow = (tokenManager: TokenManager) => {
	const userRoleClaim: string | string[] =
		tokenManager?.claims?.[ClaimTypes.ProtolabsUserRole] ?? '';
	const userRolesArray = Array.isArray(userRoleClaim)
		? userRoleClaim
		: userRoleClaim.split(',').map((r) => r.trim());
	if (userRolesArray.some((ur) => ['Guest', 'Demo'].includes(ur))) {
		return guestInit().then((guestModule) => guestModule.loadApp());
	} else {
		return mainInit().then((mainModule) => mainModule.loadApp());
	}
};

/**
 * Invoke to conditionally load the main app, guest app. Will trigger redirect
 * to sign in if the user is not authenticated.
 */
export const loadAppOrGuest = () => {
	const tokenManager = TokenManagerFactory.getOrCreateTokenManager(tokenManagerConfig);
	tokenManager
		.initializeAsync()
		.then((isAuthenticated: boolean) => {
			if (isAuthenticated) {
				return runAuthFlow(tokenManager);
			} else {
				return runSignInFlow(tokenManager);
			}
		})
		.catch((error) => {
			console.error(error);
			window.location.replace('/');
		});
};

if (
	window.location.href.toLowerCase().indexOf(tokenManagerConfig?.redirectUri?.toLowerCase()) === 0
) {
	// If handshaking, then complete auth and send the user to their original entry url
	completeAuth();
} else {
	loadAppOrGuest();
}
